import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { SignalingClient, SignalingError } from '@/features/signaling/client'
import {
  RelayClient,
  streamDownload,
  triggerBlobDownload,
  xhrUpload,
  type RelayTransferItem,
} from '@/features/relay/client'
import { humanizeError } from '@/lib/errors'
import { copyText } from '@/lib/clipboard'
import { buildRoomShareUrl, isValidRoomCode, normalizeRoomCode } from '@/lib/room-link'
import { RELAY_MAX_FILE_BYTES } from '@webfile/protocol'

export type Role = 'host' | 'guest'

/** Persisted session key for idempotent re-join / host resume across reloads. */
const SESSION_KEY = 'wf_session'
interface StoredSession {
  role: Role
  code: string
  clientId: string
  expiresAt: string
}
function readSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}
function writeSession(s: StoredSession | null) {
  try {
    if (s) sessionStorage.setItem(SESSION_KEY, JSON.stringify(s))
    else sessionStorage.removeItem(SESSION_KEY)
  } catch {
    /* ignore */
  }
}

const signaling = new SignalingClient()
const relay = new RelayClient()

/**
 * Survive React StrictMode remounts and concurrent effects. Without this,
 * auto-join can fire twice and occupy the guest slot on the same device.
 */
const autoJoinLocks = new Set<string>()
const joinInFlight = new Map<string, Promise<void>>()

function initialRoomFromUrl(): string {
  try {
    return normalizeRoomCode(new URLSearchParams(location.search).get('room') || '')
  } catch {
    return ''
  }
}

function minutesLeft(expiresAt?: string | null): number | null {
  if (!expiresAt) return null
  const ms = Date.parse(expiresAt) - Date.now()
  if (!Number.isFinite(ms)) return null
  return Math.max(0, Math.ceil(ms / 60_000))
}

export function useTransferApp() {
  const [role, setRole] = useState<Role | null>(null)
  const [roomCode, setRoomCode] = useState('')
  const [joinCode, setJoinCode] = useState(initialRoomFromUrl)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [ready, setReady] = useState(false)
  const [items, setItems] = useState<RelayTransferItem[]>([])

  const itemsMapRef = useRef(new Map<string, RelayTransferItem>())
  const roleRef = useRef<Role | null>(null)
  const activeCodeRef = useRef('')
  const clientIdRef = useRef('')
  const expiresAtRef = useRef<string | null>(null)
  const lastFileRevRef = useRef(0)
  const autoJoinAttemptedRef = useRef(false)

  const syncItems = useCallback(() => {
    setItems([...itemsMapRef.current.values()].sort((a, b) => (a.at < b.at ? 1 : -1)))
  }, [])

  const setItem = useCallback(
    (id: string, patch: Partial<RelayTransferItem>) => {
      const cur = itemsMapRef.current.get(id)
      if (!cur) return
      itemsMapRef.current.set(id, { ...cur, ...patch })
      syncItems()
    },
    [syncItems],
  )

  const notifyError = useCallback((err: unknown, fallback = '操作失败') => {
    toast.error(humanizeError(err) || fallback)
  }, [])

  const disconnect = useCallback(() => {
    itemsMapRef.current.clear()
    setItems([])
    setReady(false)
    setRole(null)
    setRoomCode('')
    setExpiresAt(null)
    const code = roomCode
    if (code) autoJoinLocks.delete(normalizeRoomCode(code))
    writeSession(null)
    try {
      history.replaceState(null, '', location.pathname)
    } catch {
      /* ignore */
    }
  }, [roomCode])

  /** Accept a pending peer file: stream it down, save automatically, free cloud. */
  const acceptFile = useCallback(
    async (item: RelayTransferItem) => {
      const code = activeCodeRef.current
      if (!code) return
      const url = relay.downloadUrl(code, item.id, item.name)
      let lastErr: unknown
      // Interrupted download: cloud copy is kept until success, so retrying is safe.
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const blob = await streamDownload(url, (received) =>
            setItem(item.id, { transferred: received, status: 'downloading' }),
          )
          const blobUrl = URL.createObjectURL(blob)
          setItem(item.id, { transferred: blob.size, status: 'done', blobUrl })
          // Save directly to the browser's downloads — no manual button.
          triggerBlobDownload(blobUrl, item.name)
          // Download complete → delete the cloud copy + manifest entry (frees quota).
          const a = { clientId: clientIdRef.current, role: roleRef.current ?? 'host' }
          await relay.delete(code, item.id, a).catch(() => {})
          return
        } catch (e) {
          lastErr = e
          if (attempt < 2) await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)))
        }
      }
      setItem(item.id, { status: 'error', error: String(lastErr) })
    },
    [setItem],
  )

  /** Decline a pending peer file (leave the cloud copy for expiry cleanup). */
  const rejectFile = useCallback(
    (item: RelayTransferItem) => {
      setItem(item.id, { status: 'rejected' })
    },
    [setItem],
  )

  const fetchManifestAndMerge = useCallback(async () => {
    const code = activeCodeRef.current
    if (!code) return
    const roleNow = roleRef.current
    const manifest = await relay.manifest(code)
    for (const f of manifest.files || []) {
      if (itemsMapRef.current.has(f.id)) continue
      const isMine = f.from === roleNow
      const item: RelayTransferItem = {
        ...f,
        // Peer files appear as "pending" — the receiver accepts first.
        status: isMine ? 'done' : 'pending',
        transferred: isMine ? f.size : 0,
      }
      itemsMapRef.current.set(f.id, item)
      syncItems()
    }
  }, [syncItems])

  // SSE primary + adaptive fallback poll (fast when SSE down, rare when up).
  useEffect(() => {
    const code = activeCodeRef.current
    if (!code || !ready) return
    let stopped = false
    let sseOk = false
    let es: EventSource | null = null
    let timer = 0

    const manifestChanged = async (rev: number) => {
      if (rev !== lastFileRevRef.current) {
        lastFileRevRef.current = rev
        await fetchManifestAndMerge()
      }
    }

    const poll = async () => {
      if (stopped) return
      if (typeof document !== 'undefined' && document.hidden) return
      const exp = expiresAtRef.current
      if (exp && Date.parse(exp) <= Date.now()) {
        stopped = true
        return
      }
      try {
        const state = await signaling.getRoom(code)
        await manifestChanged(state.fileRev ?? 0)
      } catch {
        /* transient */
      }
    }

    // SSE primary
    try {
      es = new EventSource(`/api/rooms/${encodeURIComponent(code)}/events`)
      es.onopen = () => {
        sseOk = true
      }
      es.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data)
          if (msg.type === 'files-rev') void manifestChanged(msg.fileRev)
        } catch {
          /* ignore malformed */
        }
      }
      es.onerror = () => {
        sseOk = false
      }
    } catch {
      sseOk = false
    }

    // Adaptive fallback: fast when SSE is down, rare safety net when up.
    const tick = async () => {
      if (stopped) return
      if (!sseOk) await poll()
      timer = window.setTimeout(tick, sseOk ? 15000 : 3000)
    }
    tick()

    return () => {
      stopped = true
      clearTimeout(timer)
      es?.close()
    }
  }, [ready, fetchManifestAndMerge])

  const establish = useCallback(
    (code: string, r: Role, expiry: string, clientId: string) => {
      roleRef.current = r
      activeCodeRef.current = code
      clientIdRef.current = clientId
      expiresAtRef.current = expiry
      lastFileRevRef.current = 0
      setRole(r)
      setRoomCode(code)
      setExpiresAt(expiry)
      setReady(true)
      writeSession({ role: r, code, clientId, expiresAt: expiry })
      try {
        history.replaceState(null, '', `?room=${normalizeRoomCode(code)}`)
      } catch {
        /* ignore */
      }
    },
    [],
  )

  const createRoom = useCallback(async () => {
    setBusy(true)
    try {
      const room = await signaling.createRoom()
      autoJoinLocks.add(room.code)
      establish(room.code, 'host', room.expiresAt, room.hostId)
      toast.success(`房间 ${room.code} 已创建`)
    } catch (e) {
      notifyError(e, '创建失败')
    } finally {
      setBusy(false)
    }
  }, [establish, notifyError])

  const joinRoomWithCode = useCallback(
    async (rawCode: string) => {
      const code = normalizeRoomCode(rawCode)
      if (!isValidRoomCode(code)) {
        toast.error('请输入 4–8 位房间短码（字母数字）')
        return
      }

      const existing = joinInFlight.get(code)
      if (existing) {
        await existing
        return
      }

      const run = (async () => {
        setBusy(true)
        setJoinCode(code)
        try {
          // Re-join with the stored guestId when refreshing the same room —
          // the server treats it as the same guest instead of room_full.
          const stored = readSession()
          const guestId =
            stored?.code === code && stored.role === 'guest' ? stored.clientId : undefined
          const joined = await signaling.joinRoom(code, guestId)
          establish(joined.code, 'guest', joined.expiresAt, joined.guestId)
          toast.success(`已加入 ${joined.code}`)
        } catch (e) {
          if (e instanceof SignalingError && e.code === 'room_full') {
            toast.error(
              '房间已被占用。请确认没有在同一浏览器开了多个标签自动加入；或让 Host 重新创建房间。',
            )
          } else {
            notifyError(e, '加入失败')
          }
        } finally {
          setBusy(false)
          joinInFlight.delete(code)
        }
      })()

      joinInFlight.set(code, run)
      await run
    },
    [establish, notifyError],
  )

  const joinRoom = useCallback(async () => {
    await joinRoomWithCode(joinCode)
  }, [joinCode, joinRoomWithCode])

  // On load, restore a session for the ?room= we landed on: resume host, or
// re-join as the same guest (idempotent). Otherwise join as a fresh guest.
  useEffect(() => {
    const code = initialRoomFromUrl()
    if (!code || !isValidRoomCode(code)) return

    const stored = readSession()
    if (stored && stored.code === code && stored.role === 'host') {
      if (Date.parse(stored.expiresAt) > Date.now()) {
        establish(code, 'host', stored.expiresAt, stored.clientId)
      }
      // Own room expired → stay idle rather than auto-join it.
      return
    }

    if (autoJoinLocks.has(code) || autoJoinAttemptedRef.current) return
    autoJoinLocks.add(code)
    autoJoinAttemptedRef.current = true

    const t = window.setTimeout(() => void joinRoomWithCode(code), 50)
    return () => clearTimeout(t)
    // intentionally once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const copyRoom = useCallback(async () => {
    if (!roomCode) return
    const link = buildRoomShareUrl(roomCode)
    const res = await copyText(link)
    if (res.ok) {
      toast.success('已复制房间链接')
      return
    }
    void navigator.clipboard.writeText(link).then(
      () => toast.success('已复制房间链接'),
      () =>
        toast('复制未成功，请手动复制', {
          description: link,
          duration: 8000,
          action: {
            label: '复制',
            onClick: () => {
              void navigator.clipboard.writeText(link)
            },
          },
        }),
    )
  }, [roomCode])

  const sendFiles = useCallback(
    async (files: FileList | File[]) => {
      const code = activeCodeRef.current
      const r = roleRef.current
      const clientId = clientIdRef.current
      if (!code || !r || !clientId) {
        toast.error('请先创建或加入房间')
        return
      }
      let list = [...files]
      const oversized = list.filter((f) => f.size > RELAY_MAX_FILE_BYTES)
      if (oversized.length) {
        toast.error(`文件过大（单文件上限约 1GB）：${oversized.map((f) => f.name).join(', ')}`)
        list = list.filter((f) => f.size <= RELAY_MAX_FILE_BYTES)
      }
      if (!list.length) return

      for (const file of list) {
        const item: RelayTransferItem = {
          id: '',
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          from: r,
          at: new Date().toISOString(),
          status: 'uploading',
          transferred: 0,
        }
        const auth = { clientId, role: r }
        try {
          const target = await relay.uploadUrl(code, { name: file.name, size: file.size }, auth)
          item.id = target.id
          itemsMapRef.current.set(item.id, item)
          syncItems()
          await xhrUpload(target.url, file, (sent) => setItem(item.id, { transferred: sent }))
          await relay.register(code, item, auth)
          setItem(item.id, { status: 'done', transferred: file.size })
          // Signal peers via the cheap room counter (best-effort).
          await signaling.notifyFile(code, clientId).catch(() => {})
        } catch (e) {
          // Interrupted upload: purge the (possibly partial) orphan blob.
          if (item.id) {
            setItem(item.id, { status: 'error', error: String(e) })
            await relay.delete(code, item.id, auth).catch(() => {})
          } else {
            toast.error(`${file.name}：${String(e)}`)
          }
        }
      }
      toast.success(`已上传 ${list.length} 个文件`)
    },
    [setItem, syncItems],
  )

  const shareUrl = roomCode ? buildRoomShareUrl(roomCode) : ''
  const expiresInMinutes = minutesLeft(expiresAt)

  return {
    role,
    roomCode,
    joinCode,
    setJoinCode,
    shareUrl,
    expiresAt,
    expiresInMinutes,
    busy,
    ready,
    items,
    createRoom,
    joinRoom,
    joinRoomWithCode,
    copyRoom,
    sendFiles,
    acceptFile,
    rejectFile,
    disconnect,
  }
}